/**
 * useTrackingSessions — keeps a live registry of every active tracking
 * session in the network, with their breadcrumb routes, ready for MapView
 * to render moving pins + polylines.
 *
 * Fetches initial state by walking active alerts (one-shot on mount; this
 * is small data — just the active sessions for the alerts already loaded).
 *
 * Listens to WS events:
 *   tracking_started  — adds a new session
 *   tracking_point    — appends a point to the matching session
 *   tracking_stopped  — removes the session from the registry
 *
 * Returns a Map<session_id, {session, points[]}>.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useWebSocket } from "./useWebSocket";
import logging from "../utils/logging";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useTrackingSessions(alerts) {
  // Map<session_id, {session, points: [{latitude, longitude, recorded_at}]}>
  const [sessions, setSessions] = useState({});
  const { subscribe } = useWebSocket() || {};

  // 1) Hydrate from current alerts (only ones that are tagged track_active).
  // The alerts collection denormalizes a `track_active` flag onto the alert
  // document when tracking starts. If absent we just don't fetch — saves
  // a per-alert burst on first paint.
  useEffect(() => {
    let cancelled = false;
    const candidates = (alerts || []).filter((a) => a.track_active);
    if (!candidates.length) return;
    (async () => {
      const next = {};
      await Promise.all(
        candidates.map(async (a) => {
          try {
            const r = await axios.get(`${API}/alerts/${a.alert_id}/track`, { withCredentials: true });
            const ses = r.data?.session;
            if (ses && ses.status === "active") {
              next[ses.session_id] = { session: ses, points: r.data.points || [] };
            }
          } catch (err) {
            logging.warn("Failed to hydrate track for alert", a.alert_id, err);
          }
        }),
      );
      if (!cancelled && Object.keys(next).length) {
        setSessions((prev) => ({ ...prev, ...next }));
      }
    })();
    return () => { cancelled = true; };
  }, [alerts]);

  // 2) Subscribe to real-time WS broadcasts
  useEffect(() => {
    if (typeof subscribe !== "function") return;
    return subscribe((msg) => {
      if (!msg || !msg.type) return;
      if (msg.type === "tracking_started") {
        setSessions((prev) => ({
          ...prev,
          [msg.session_id]: { session: msg, points: [] },
        }));
      } else if (msg.type === "tracking_point") {
        setSessions((prev) => {
          const cur = prev[msg.session_id];
          if (!cur) return prev; // session we don't know about yet
          return {
            ...prev,
            [msg.session_id]: {
              ...cur,
              points: [
                ...cur.points,
                {
                  latitude: msg.latitude,
                  longitude: msg.longitude,
                  recorded_at: msg.recorded_at,
                  accuracy: msg.accuracy,
                  speed: msg.speed,
                },
              ],
            },
          };
        });
      } else if (msg.type === "tracking_stopped") {
        setSessions((prev) => {
          if (!prev[msg.session_id]) return prev;
          const next = { ...prev };
          delete next[msg.session_id];
          return next;
        });
      }
    });
  }, [subscribe]);

  return sessions;
}
